import { useState, useEffect } from 'react';
import type { Media } from '@wineryxplorer/shared';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';
import { MediaDetailCaptionEditor } from './MediaDetailCaptionEditor';
import { MediaDetailMetadata } from './MediaDetailMetadata';
import { MediaDetailTagsEditor } from './MediaDetailTagsEditor';

export interface MediaDetailProps {
  media: Media | null;
  isOpen: boolean;
  onClose: () => void;
  onPublish?: (mediaId: string) => void;
  onUnpublish?: (mediaId: string) => void;
  onSetAsHero?: (mediaId: string) => void;
  onAddToGallery?: (mediaId: string) => void;
  onRemoveFromGallery?: (mediaId: string) => void;
  onDelete?: (mediaId: string) => void;
  onUpdateTags?: (mediaId: string, tags: string[]) => void;
  onUpdateCaption?: (mediaId: string, caption: string) => void;
  onUseSharedMedia?: (sharedMediaId: string, action: 'setAsHero' | 'addToGallery') => void;
  isLoading?: boolean;
  canSetHero?: boolean;
  canManageGallery?: boolean;
  venueId?: string; // Required for shared media actions
}

export function MediaDetail({
  media,
  isOpen,
  onClose,
  onPublish,
  onUnpublish,
  onSetAsHero,
  onAddToGallery,
  onRemoveFromGallery,
  onDelete,
  onUpdateTags,
  onUpdateCaption,
  onUseSharedMedia,
  isLoading = false,
  canSetHero = true,
  canManageGallery = true,
  venueId,
}: MediaDetailProps) {
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editCaption, setEditCaption] = useState('');
  const [newTag, setNewTag] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset state when media changes
  useEffect(() => {
    if (media) {
      setEditTags(media.tags);
      setEditCaption(media.caption ?? '');
      setIsEditingTags(false);
      setIsEditingCaption(false);
      setShowDeleteConfirm(false);
    }
  }, [media]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !media) {
    return null;
  }

  const handleSaveTags = () => {
    if (onUpdateTags) {
      onUpdateTags(media.id, editTags);
    }
    setIsEditingTags(false);
  };

  const handleCancelEditTags = () => {
    setEditTags(media.tags);
    setIsEditingTags(false);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editTags.includes(newTag.trim())) {
      setEditTags([...editTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };

  const handleSaveCaption = () => {
    if (onUpdateCaption) {
      onUpdateCaption(media.id, editCaption);
    }
    setIsEditingCaption(false);
  };

  const handleCancelEditCaption = () => {
    setEditCaption(media.caption ?? '');
    setIsEditingCaption(false);
  };

  const handleTogglePublication = () => {
    if (media.isPublished && onUnpublish) {
      onUnpublish(media.id);
    } else if (!media.isPublished && onPublish) {
      onPublish(media.id);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(media.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gray-50 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {media.filename}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex flex-col lg:flex-row max-h-[calc(90vh-4rem)]">
            {/* Image Preview */}
            <div className="flex-1 bg-gray-100 flex items-center justify-center p-4">
              <ImgWithSkeleton
                src={media.cloudinaryUrl}
                alt={media.caption || media.filename}
                className="max-w-full max-h-full object-contain rounded"
                skeletonClassName="rounded"
              />
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-80 border-l border-gray-200 flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Media Type Indicator */}
                {media.entityType === 'shared' && (
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-primary-700 mr-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-primary-800">Shared Media</p>
                        <p className="text-xs text-primary-700">Available to all venue managers</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Publication Status - Only for non-shared media */}
                {media.entityType !== 'shared' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Status</span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          media.isPublished
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {media.isPublished ? 'Published' : 'Unpublished'}
                      </span>
                    </div>
                    <button
                      onClick={handleTogglePublication}
                      disabled={isLoading}
                      className={`w-full px-3 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
                        media.isPublished
                          ? 'text-gray-700 bg-gray-50 border border-gray-300 hover:bg-gray-50'
                          : 'text-white bg-green-600 border border-transparent hover:bg-green-700'
                      }`}
                    >
                      {media.isPublished ? 'Unpublish' : 'Publish'}
                    </button>
                  </div>
                )}

                {/* Caption */}
                <MediaDetailCaptionEditor
                  caption={media.caption}
                  isEditing={isEditingCaption}
                  value={editCaption}
                  isLoading={isLoading}
                  onChange={setEditCaption}
                  onStartEdit={() => setIsEditingCaption(true)}
                  onSave={handleSaveCaption}
                  onCancel={handleCancelEditCaption}
                />

                {/* Tags */}
                <MediaDetailTagsEditor
                  tags={media.tags}
                  isEditing={isEditingTags}
                  editTags={editTags}
                  newTag={newTag}
                  isLoading={isLoading}
                  onNewTagChange={setNewTag}
                  onAddTag={handleAddTag}
                  onRemoveTag={handleRemoveTag}
                  onStartEdit={() => setIsEditingTags(true)}
                  onSave={handleSaveTags}
                  onCancel={handleCancelEditTags}
                />

                {/* Venue Actions */}
                {canSetHero && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 block mb-2">
                      {media.entityType === 'shared' ? 'Use Shared Media' : 'Venue Actions'}
                    </span>
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          if (media.entityType === 'shared' && onUseSharedMedia && venueId) {
                            onUseSharedMedia(media.id, 'setAsHero');
                          } else {
                            onSetAsHero?.(media.id);
                          }
                        }}
                        disabled={isLoading || (media.entityType === 'shared' && !venueId)}
                        className="w-full px-3 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                      >
                        Set as Hero Image
                      </button>
                      {canManageGallery && (
                        <>
                          <button
                            onClick={() => {
                              if (media.entityType === 'shared' && onUseSharedMedia && venueId) {
                                onUseSharedMedia(media.id, 'addToGallery');
                              } else {
                                onAddToGallery?.(media.id);
                              }
                            }}
                            disabled={isLoading || (media.entityType === 'shared' && !venueId)}
                            className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                          >
                            Add to Gallery
                          </button>
                          {media.entityType !== 'shared' && (
                            <button
                              onClick={() => onRemoveFromGallery?.(media.id)}
                              disabled={isLoading}
                              className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                            >
                              Remove from Gallery
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {media.entityType === 'shared' && (
                      <p className="text-xs text-gray-500 mt-2">
                        This will reference the shared image without copying it
                      </p>
                    )}
                  </div>
                )}

                {/* Metadata */}
                <MediaDetailMetadata media={media} />

                {/* Delete - Only for non-shared media */}
                {media.entityType !== 'shared' && (
                  <div className="pt-4 border-t border-gray-200">
                    {!showDeleteConfirm ? (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isLoading}
                        className="w-full px-3 py-2 text-sm font-medium text-red-700 bg-gray-50 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
                      >
                        Delete Media
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm text-red-700">
                          Are you sure you want to delete this media? This action cannot be undone.
                        </p>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleDelete}
                            disabled={isLoading}
                            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={isLoading}
                            className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}